import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, createCustomer, fetchCustomers, updateCustomer } from '@/api/client'
import { EmptyState } from '@/components/EmptyState'
import { LoadingState } from '@/components/LoadingState'
import { formatDate } from '@/lib/format-date'

import { Building2 } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

export function CustomersAdminPage() {
  const qc = useQueryClient()
  const customersQuery = useQuery({
    queryKey: ['customers', 'admin'],
    queryFn: fetchCustomers,
  })
  const [error, setError] = useState<string | null>(null)
  const [newName, setNewName] = useState('')

  const createMut = useMutation({
    mutationFn: createCustomer,
    onSuccess: async () => {
      setError(null)
      setNewName('')
      await qc.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create customer')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateCustomer(id, { name }),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update customer')
    },
  })

  const customers = customersQuery.data ?? []

  return (
    <AdminSection
      title="Customers"
      tone="emerald"
      icon={Building2}
      lede="Finance-maintained customer catalogue (BR-36)."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <form
        className="admin-form admin-form--inline"
        onSubmit={(event) => {
          event.preventDefault()
          const name = newName.trim()
          if (name) createMut.mutate({ name })
        }}
      >
        <label className="field">
          <span className="field__label">New customer</span>
          <input
            className="field__input"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            maxLength={200}
            required
          />
        </label>
        <button type="submit" className="button button--primary" disabled={createMut.isPending}>
          Add
        </button>
      </form>

      {customersQuery.isLoading && (
        <LoadingState label="Loading customers…" variant="skeleton-table" rows={6} columns={3} />
      )}

      {!customersQuery.isLoading && customers.length === 0 && (
        <EmptyState title="No customers yet." />
      )}

      {customers.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Created</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <CustomerRow
                  key={customer.id}
                  id={customer.id}
                  name={customer.name}
                  createdAt={customer.created_at}
                  busy={patchMut.isPending}
                  onSave={(name) => patchMut.mutate({ id: customer.id, name })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  )
}

function CustomerRow({
  id,
  name,
  createdAt,
  busy,
  onSave,
}: {
  id: string
  name: string
  createdAt?: string
  busy: boolean
  onSave: (name: string) => void
}) {
  const [draft, setDraft] = useState(name)

  return (
    <tr>
      <td>
        <input
          className="field__input field__input--compact"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label={`Customer name ${id}`}
        />
      </td>
      <td>{formatDate(createdAt)}</td>
      <td>
        <button
          type="button"
          className="button button--ghost"
          disabled={busy || draft.trim() === name}
          onClick={() => onSave(draft.trim())}
        >
          Save
        </button>
      </td>
    </tr>
  )
}
